import { useMutation, UseMutationResult, useQueryClient } from 'react-query';

import { getTokenWithType } from './useAuth';

import { saveLocation, locationItem as updateLocation } from 'utils/urls';

import { LocationInput } from 'types/locations';
import { ErrorModal } from 'types/modalTypes';

export async function postLocation(params: LocationInput): Promise<TResult> {
  const { Id, AccountId } = params;
  const response = await fetch(
    Id === 0 ? saveLocation(AccountId) : updateLocation(AccountId, Id),
    {
      method: Id === 0 ? 'POST' : 'PUT',
      headers: {
        Authorization: getTokenWithType(),
        'content-type': 'application/json',
      },
      body: JSON.stringify(params),
    },
  );
  return response.json();
}

type TError = ErrorModal;

type TResult = {
  Data: LocationInput;
  IsSuccess: boolean;
  StatusCode: number;
} & ErrorModal;

type TVariables = LocationInput;

type TSnapshot = unknown;

function useSaveLocation(): UseMutationResult<
  TResult,
  TError,
  TVariables,
  TSnapshot
> {
  const queryClient = useQueryClient();
  return useMutation(async (params: LocationInput) => {
    const { Id, StateProvinceId, AccountId } = params;

    if (Id === 0 && StateProvinceId === 0) {
      delete params.StateProvinceId;
      delete params.StateProvince;
    }
    const newLocation = await postLocation(params);
    if (newLocation.StatusCode === 200) {
      queryClient.invalidateQueries(['locations_list']);
      queryClient.invalidateQueries(['get_location', AccountId, Id]);
      return newLocation;
    }

    if (newLocation.Errors[0].Message) throw newLocation;
    return newLocation;
  });
}

export { useSaveLocation };
